/**
 * PartDetailForm — the full editor for a single selected part, shown inside the {@link OverlayDrawer}.
 *
 * Panels shown depend on the part: always name/timing/visibility + cost; a Transport panel for transport
 * parts; an Accommodation panel (facility sub-type, category, unit patterns, equipment) for accommodation.
 * Every field writes straight back to the board model via `onUpdatePart`. The bound-placeholder banner (if
 * the part came from a placeholder) links out to the placeholder detail. Rendered keyed by uid, so it is a
 * fresh instance per opened part.
 */
import { useState } from 'react';
import { ExternalLink, User, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AccommodationUnitPattern,
  PlaceholderCostType,
  PlaceholderType,
  TransportKind,
} from '@/api/generated/schemas';
import { Button, Field, Input, Panel, SegmentedButtons, Textarea, TimeBox } from '@/shared/ui';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { placeholderDetailAgenda } from '@/modules/products/venua-planner/schemes';
import { TYPE_COLOR, TypeIcon, fmtH, type BoardPart } from '../model';
import { AmountBox } from './AmountBox';
import { AccommodationCategorySelector } from './drawerSelectors';
import { EquipmentSelector, FacilitySubTypeSelector } from './drawerSelectors';
import { usePlaceholderBackfill } from './usePlaceholderBackfill';
import { CreatePlaceholderFromPartDialog } from './CreatePlaceholderFromPartDialog';

/** Left-label column width — shared by every inline field so the label gutter stays aligned. */
const LABEL_W = 140;

interface PartDetailFormProps {
  part: BoardPart;
  readOnly: boolean;
  onClose: () => void;
  onUpdatePart: (uid: string, patch: Partial<BoardPart>) => void;
  onDeletePart: (uid: string) => void;
}

/** `"HH:mm"` → fractional hours, or `null` when unparseable. */
function parseHhmm(value: string): number | null {
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h)) return null;
  return h + (Number.isNaN(m) ? 0 : m / 60);
}

export function PartDetailForm({
  part,
  readOnly,
  onClose,
  onUpdatePart,
  onDeletePart,
}: PartDetailFormProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();

  const {
    placeholder,
    facilityRef,
    setFacilityRef,
    categoryRef,
    setCategoryRef,
    equipmentRefs,
    setEquipmentRefs,
  } = usePlaceholderBackfill(part);

  const update = (patch: Partial<BoardPart>) => onUpdatePart(part.uid, patch);
  const [createOpen, setCreateOpen] = useState(false);

  const isTimed = part.kind === 'timed';
  const isAccommodation = part.type === PlaceholderType.Accommodation;
  const isTransport = part.type === PlaceholderType.Transport;
  const hasCost = part.costType !== PlaceholderCostType.None;
  const startHour = part.startHour ?? 0;

  const typeLabel = t(`costScheme.types.${part.type}`);
  const subcategory = placeholder?.subcategoryName ?? null;
  const programTitle = (
    <span className="inline-flex items-center gap-1.5">
      <span style={{ color: TYPE_COLOR[part.type], display: 'inline-flex' }}>
        <TypeIcon type={part.type} transportKind={part.transportKind} size={14} />
      </span>
      <span>{subcategory ? `${typeLabel} · ${subcategory}` : typeLabel}</span>
    </span>
  );

  const costTypeOptions = [
    { value: PlaceholderCostType.Calculation, label: t('costScheme.costTypes.Calculation') },
    { value: PlaceholderCostType.Onsite, label: t('costScheme.costTypes.Onsite') },
    { value: PlaceholderCostType.None, label: t('costScheme.costTypes.None') },
  ];
  const transportOptions = [
    { value: TransportKind.Bus, label: t('costScheme.transportKinds.Bus') },
    { value: TransportKind.Boat, label: t('costScheme.transportKinds.Boat') },
    { value: TransportKind.Train, label: t('costScheme.transportKinds.Train') },
  ];
  const unitPatternOptions = [
    { value: AccommodationUnitPattern.Single, label: t('costScheme.unitPatterns.Single') },
    { value: AccommodationUnitPattern.Double, label: t('costScheme.unitPatterns.Double') },
    { value: AccommodationUnitPattern.Triple, label: t('costScheme.unitPatterns.Triple') },
    { value: AccommodationUnitPattern.Quad, label: t('costScheme.unitPatterns.Quad') },
  ];

  const rangeLabel = isTimed
    ? `${t('costScheme.day')} ${part.day + 1}`
    : isAccommodation
      ? part.span > 1
        ? `${t('costScheme.nights')} ${part.day + 1}–${part.day + part.span}`
        : `${t('costScheme.night')} ${part.day + 1}`
      : part.span > 1
        ? `${t('costScheme.days')} ${part.day + 1}–${part.day + part.span}`
        : `${t('costScheme.day')} ${part.day + 1}`;

  const openPlaceholder = () => {
    if (!part.sourcePlaceholderId) return;
    void navigator.openModal(placeholderDetailAgenda, { placeholderId: part.sourcePlaceholderId });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-phx-border gap-phx-3 px-phx-5 flex flex-none items-center border-b py-2.5">
        <span className="text-phx-md text-phx-text min-w-0 flex-1 truncate font-bold">
          {part.name || t('costScheme.drawer.untitled')}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t('costScheme.drawer.close')}
        >
          <X size={16} />
        </Button>
      </div>

      {/* Body */}
      <div className="gap-phx-4 p-phx-5 flex min-h-0 flex-1 flex-col overflow-y-auto">
        {part.sourcePlaceholderId ? (
          <div className="border-phx-border bg-phx-surface-2 text-phx-sm text-phx-text-2 gap-phx-3 rounded-phx-md px-phx-4 flex items-center border py-1.75">
            <span
              className="h-2 w-2 flex-none rounded-full"
              style={{ background: TYPE_COLOR[part.type] }}
            />
            <span className="min-w-0 flex-1 truncate">
              {t('costScheme.drawer.boundPlaceholder')}{' '}
              <b className="text-phx-text font-semibold">{placeholder?.displayName ?? part.name}</b>
            </span>
            {placeholder?.code ? (
              <span className="text-phx-text-muted text-phx-xs flex-none font-mono">
                {placeholder.code}
              </span>
            ) : null}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={openPlaceholder}
              title={t('costScheme.drawer.openPlaceholder')}
              aria-label={t('costScheme.drawer.openPlaceholder')}
            >
              <ExternalLink size={14} />
            </Button>
          </div>
        ) : readOnly ? null : (
          <div className="border-phx-border bg-phx-surface-2 gap-phx-3 rounded-phx-md px-phx-4 flex items-center border border-dashed py-1.75">
            <span className="bg-phx-text-muted/40 h-2 w-2 flex-none rounded-full" />
            <span className="text-phx-sm text-phx-text-2 min-w-0 flex-1">
              {t('costScheme.drawer.noSourcePlaceholder')}
            </span>
            <Button variant="primary" size="sm" iconLeft="plus" onClick={() => setCreateOpen(true)}>
              {t('costScheme.drawer.createPlaceholder')}
            </Button>
          </div>
        )}

        {/* Program — name, timing, visibility */}
        <Panel variant="filled" title={programTitle}>
          <div className="gap-phx-3 flex flex-col">
            <Field label={t('costScheme.drawer.name')} layout="inline" labelWidth={LABEL_W}>
              <Input
                value={part.name}
                disabled={readOnly}
                onChange={(event) => update({ name: event.target.value })}
              />
            </Field>
            <Field
              label={
                isAccommodation
                  ? t('costScheme.drawer.nightsLabel')
                  : t('costScheme.drawer.rangeLabel')
              }
              layout="inline"
              labelWidth={LABEL_W}
            >
              <span className="text-phx-sm text-phx-text h-phx-control flex items-center font-mono">
                {rangeLabel}
              </span>
            </Field>
            {isTimed ? (
              <>
                <Field
                  label={t('costScheme.drawer.start')}
                  layout="inline"
                  labelWidth={LABEL_W}
                  className="max-w-82.5"
                >
                  <TimeBox
                    value={fmtH(startHour)}
                    disabled={readOnly}
                    onChange={(next) => {
                      const hours = parseHhmm(next);
                      if (hours != null) update({ startHour: hours });
                    }}
                  />
                </Field>
                <Field
                  label={t('costScheme.drawer.duration')}
                  layout="inline"
                  labelWidth={LABEL_W}
                  className="max-w-82.5"
                >
                  <Input
                    className="tp-nospin"
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={part.hours}
                    unit="h"
                    disabled={readOnly}
                    onChange={(event) => {
                      const value = Number(event.target.value);
                      if (!Number.isNaN(value)) update({ hours: value });
                    }}
                  />
                </Field>
              </>
            ) : null}
            <Field
              label={t('costScheme.drawer.showInProgram')}
              layout="inline"
              labelWidth={LABEL_W}
            >
              <SegmentedButtons
                value={part.showInProgram ? 'yes' : 'no'}
                disabled={readOnly}
                options={[
                  { value: 'yes', label: t('costScheme.yes') },
                  { value: 'no', label: t('costScheme.no') },
                ]}
                onChange={(value) => update({ showInProgram: value === 'yes' })}
              />
            </Field>
          </div>
        </Panel>

        {/* Cost — pax + group (box-only) */}
        <Panel variant="filled" title={t('costScheme.drawer.costPanel')}>
          <div className="gap-phx-3 flex flex-col">
            <Field label={t('costScheme.drawer.costType')} layout="inline" labelWidth={LABEL_W}>
              <SegmentedButtons
                value={part.costType}
                disabled={readOnly}
                options={costTypeOptions}
                onChange={(value) => update({ costType: value })}
              />
            </Field>
            {hasCost ? (
              <>
                <Field
                  label={t('costScheme.drawer.costPax')}
                  layout="inline"
                  labelWidth={LABEL_W}
                  className="max-w-90"
                >
                  <AmountBox
                    icon={<User size={15} />}
                    value={part.costPax}
                    step={10}
                    currency={part.currency}
                    readOnly={readOnly}
                    onValueChange={(value) => update({ costPax: value })}
                    onCurrencyChange={(currency) => update({ currency })}
                  />
                </Field>
                <Field
                  label={t('costScheme.drawer.costGroup')}
                  layout="inline"
                  labelWidth={LABEL_W}
                  className="max-w-90"
                >
                  <AmountBox
                    icon={<Users size={15} />}
                    value={part.costGroup}
                    step={100}
                    currency={part.currency}
                    readOnly={readOnly}
                    onValueChange={(value) => update({ costGroup: value })}
                    onCurrencyChange={(currency) => update({ currency })}
                  />
                </Field>
              </>
            ) : null}
            <div className="text-phx-xs text-phx-text-muted" style={{ paddingLeft: LABEL_W + 12 }}>
              {t(`costScheme.costHint.${part.costType}`)}
            </div>
          </div>
        </Panel>

        {/* Type-specific parameters */}
        {isTransport ? (
          <Panel variant="filled" title={t('costScheme.drawer.transportPanel')}>
            <Field
              label={t('costScheme.drawer.transportKind')}
              layout="inline"
              labelWidth={LABEL_W}
            >
              <SegmentedButtons
                value={part.transportKind ?? ''}
                disabled={readOnly}
                options={transportOptions}
                onChange={(value) => update({ transportKind: value as TransportKind })}
              />
            </Field>
          </Panel>
        ) : null}

        {isAccommodation ? (
          <Panel variant="filled" title={t('costScheme.drawer.accommodationPanel')}>
            <div className="gap-phx-3 flex flex-col">
              <Field
                label={t('costScheme.drawer.facilitySubType')}
                layout="inline"
                labelWidth={LABEL_W}
              >
                <FacilitySubTypeSelector
                  mode="single"
                  label={t('costScheme.drawer.facilitySubType')}
                  selected={facilityRef ? [facilityRef] : []}
                  onChange={(next) => {
                    const ref = next[0] ?? null;
                    setFacilityRef(ref);
                    update({
                      facilitySubTypeId: ref?.id ?? null,
                      facilitySubTypeName: ref?.name ?? null,
                    });
                  }}
                />
              </Field>
              <Field
                label={t('costScheme.drawer.accommodationCategory')}
                layout="inline"
                labelWidth={LABEL_W}
              >
                <AccommodationCategorySelector
                  mode="single"
                  label={t('costScheme.drawer.accommodationCategory')}
                  selected={categoryRef ? [categoryRef] : []}
                  onChange={(next) => {
                    const ref = next[0] ?? null;
                    setCategoryRef(ref);
                    update({
                      accommodationCategoryCbId: ref?.id ?? null,
                      accommodationCategoryName: ref?.name ?? null,
                    });
                  }}
                />
              </Field>
              <Field
                label={t('costScheme.drawer.unitPatterns')}
                layout="inline"
                labelWidth={LABEL_W}
              >
                <SegmentedButtons
                  multiple
                  value={part.unitPatterns}
                  disabled={readOnly}
                  options={unitPatternOptions}
                  onChange={(value) =>
                    update({ unitPatterns: value as AccommodationUnitPattern[] })
                  }
                />
              </Field>
              <Field label={t('costScheme.drawer.equipment')} layout="inline" labelWidth={LABEL_W}>
                <EquipmentSelector
                  mode="multi"
                  label={t('costScheme.drawer.equipment')}
                  selected={equipmentRefs}
                  onChange={(next) => {
                    setEquipmentRefs([...next]);
                    update({ equipmentIds: next.map((ref) => ref.id) });
                  }}
                />
              </Field>
            </div>
          </Panel>
        ) : null}

        {/* Note */}
        <Panel variant="filled" title={t('costScheme.drawer.notePanel')}>
          <Textarea
            rows={3}
            placeholder={t('costScheme.drawer.notePlaceholder')}
            value={part.note ?? ''}
            disabled={readOnly}
            onChange={(event) => update({ note: event.target.value })}
          />
        </Panel>

        {readOnly ? null : (
          <div className="flex justify-end">
            <Button
              variant="danger"
              size="sm"
              iconLeft="trash"
              onClick={() => {
                onDeletePart(part.uid);
                onClose();
              }}
            >
              {t('costScheme.drawer.delete')}
            </Button>
          </div>
        )}
      </div>

      {createOpen ? (
        <CreatePlaceholderFromPartDialog
          part={part}
          onClose={() => setCreateOpen(false)}
          onCreated={(placeholderId) => update({ sourcePlaceholderId: placeholderId })}
        />
      ) : null}
    </div>
  );
}
